/**
 * useDomainTypes — subscribes to a domain's types collection in real-time.
 *
 * Returns the types array and loading state. Automatically unsubscribes
 * when the domain changes or the component unmounts.
 */

import { useState, useEffect } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../../firebase';
import { dynamicDomainListConstraints } from '@/services/domain/dynamicDomainQueries';

/**
 * @param {string} schoolId
 * @param {Object|null} domain - Active domain config (must have collections.types)
 * @returns {{ types: Array, isLoading: boolean }}
 */
export function useDomainTypes(schoolId, domain) {
  const [types, setTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const typesCollection = domain?.collections?.types;

  useEffect(() => {
    if (!schoolId || !typesCollection) {
      setTypes([]);
      return;
    }

    setIsLoading(true);

    const colRef = collection(db, `schools/${schoolId}/${typesCollection}`);
    const typesQuery = query(
      colRef,
      ...dynamicDomainListConstraints({
        collectionName: typesCollection,
        schoolId,
        domainId: domain?.id,
      }),
    );

    const unsub = onSnapshot(typesQuery, (snap) => {
      const items = snap.docs
        .map((d) => ({ ...d.data(), id: d.id }))
        .filter((type) => type.retired !== true);
      // Sort by sortOrder if present, otherwise by label/name
      items.sort((a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999));
      setTypes(items);
      setIsLoading(false);
    }, (err) => {
      console.error('useDomainTypes subscription error:', err);
      setIsLoading(false);
    });

    return unsub;
  }, [schoolId, domain?.id, typesCollection]);

  return { types, isLoading };
}
